import axios                                         from 'axios';
import { FC, useMemo }                               from 'react';
import { Navigate, Route, Routes }                   from 'react-router-dom';
import { AuthRoutes }                                from './Auth/AuthRoutes';
import { ActivityRoutes }                            from './modules/Activity/ActivityRoutes';
import { AddMembersRoutes }                          from './modules/Dashboard/AddMembers/AddMembersRoutes';
import { DashboardRoutes }                           from './modules/Dashboard/DashboardRoutes';
import { LocateRoutes }                              from './modules/Locate/LocateRoutes';
import { MessageCenter }                             from './modules/MessageCenter/MessageCenter';
import { MyFamilyCoreRoutes }                        from './modules/MyFamilyCore/MyFamilyCoreRoutes';
import { NotificationPreferences }                   from './modules/NotificationPreferences/NotificationPreferences';
import { ScheduleRoutes }                            from './modules/Schedule/ScheduleRoutes';
import { SettingsRoutes }                            from './modules/Settings/SettingsRoutes';
import { TermsAndPrivacyPage }                       from './modules/TermsAndPrivacy/TermsAndPrivacyPage';
import { NotFound }                                  from './shared/components/NotFound';
import { useFetchSelectedFamily }                    from './shared/components/SelectFamily/queries';
import { EAppRoutes, EAuthRoutes, familyCoreConfig } from './shared/constants/constants';
import { withQueryParams }                           from './shared/functions';
import { ITokens }                                   from './shared/interfaces/auth';
import { useFetchFamilyUser }                        from './shared/queries/family';
import { AuthorizationProvider }                     from './shared/utils/withAuthorization/authorizationContext';
import { AdminStatistics }                           from './shared/components/AdminStatistics/AdminStatistics';
import { getAuthToken }                              from './utils/authHelpers';
import { PrivateRoute }                              from './PrivateRoute';

const authToken: ITokens = getAuthToken();

export const AppRoutes: FC = () => {
  const loggedIn = !!axios.defaults.headers.common.authorization;
  const family = useFetchSelectedFamily({ enabled: loggedIn });
  const userProfile = useFetchFamilyUser(family?.data?.id, { enabled: loggedIn && !!family?.data?.id }) as any;
  const baseRoute = useMemo(() => {
    if (!authToken?.accessToken) {
      return EAuthRoutes.SignIn;
    }

    return EAuthRoutes.SelectFamilyComponent;
  }, [family]);

  return (
    <AuthorizationProvider value={userProfile}>
      <Routes>
        <Route
          path="/"
          element={<Navigate to={withQueryParams(baseRoute)} />}
        />

        <Route
          path={EAppRoutes.Terms}
          element={<TermsAndPrivacyPage />}
        />

        <Route
          path="auth/*"
          element={<AuthRoutes />}
        />

        <Route
          path={`${EAppRoutes.Settings}/*`}
          element={<PrivateRoute><SettingsRoutes /></PrivateRoute>}
        />

        <Route
          path={`${EAppRoutes.Dashboard}/*`}
          element={<PrivateRoute><DashboardRoutes /></PrivateRoute>}
        />

        <Route
          path={`${EAppRoutes.Activity}/*`}
          element={<PrivateRoute><ActivityRoutes /></PrivateRoute>}
        />

        <Route
          path={`${EAppRoutes.Schedule}/*`}
          element={<PrivateRoute><ScheduleRoutes /></PrivateRoute>}
        />

        <Route
          path={`${EAppRoutes.Locate}/*`}
          element={<PrivateRoute><LocateRoutes /></PrivateRoute>}
        />

        { familyCoreConfig.isChatEnabled && (
          <Route
            path={EAppRoutes.MessageCenter}
            element={<PrivateRoute><MessageCenter /></PrivateRoute>}
          />
        ) }

        <Route
          path={`${EAppRoutes.MyFamilyCore}/*`}
          element={<PrivateRoute><MyFamilyCoreRoutes /></PrivateRoute>}
        />

        <Route
          path={`${EAppRoutes.AddMembers}/*`}
          element={<PrivateRoute><AddMembersRoutes /></PrivateRoute>}
        />

        <Route
          path={EAppRoutes.NotificationPreferences}
          element={<PrivateRoute><NotificationPreferences /></PrivateRoute>}
        />

        <Route
          path={`${EAppRoutes.AdminStatistics}/*`}
          element={<PrivateRoute><AdminStatistics /></PrivateRoute>}
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthorizationProvider>
  );
};
